import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SELLER_STATUS = { pending: "En attente", approved: "Approuvé", rejected: "Refusé", suspended: "Suspendu" };
const PROD_STATUS = { draft: "Brouillon", pending: "En attente", published: "Publié", rejected: "Refusé", archived: "Archivé" };
const ORDER_STATUS = ["paid", "preparing", "shipped", "delivered", "cancelled", "refunded"];
const ORDER_FR = { paid: "Payée", preparing: "En préparation", shipped: "Expédiée", delivered: "Livrée", cancelled: "Annulée", refunded: "Remboursée", pending: "En attente" };

function Stat({ label, value, accent }) {
  return <div className={`p-5 ${accent ? "bg-[#0A0A0A] text-white" : "border border-neutral-100"}`}>
    <p className={`over text-[0.55rem] ${accent ? "text-[#D4AF37]" : "text-neutral-400"}`}>{label}</p>
    <p className="font-display text-3xl mt-1">{value}</p></div>;
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ commission_rate: 15 });
  const [reject, setReject] = useState(null); // {type, id}
  const [reason, setReason] = useState("");

  const reload = useCallback(async () => {
    const [st, se, pr, or_, us, rv, sg] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/sellers"), api.get("/admin/products"),
      api.get("/admin/orders"), api.get("/admin/users"), api.get("/admin/reviews"), api.get("/admin/settings"),
    ]);
    setStats(st.data); setSellers(se.data.sellers); setProducts(pr.data.products);
    setOrders(or_.data.orders); setUsers(us.data.users); setReviews(rv.data.reviews); setSettings(sg.data.settings);
  }, []);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) { nav("/connexion"); return; }
    if (user && user.role === "admin") reload().catch(() => {});
  }, [user, ready, nav, reload]);

  if (!user || user.role !== "admin" || !stats) return <div className="h-[50vh]" />;

  const sellerStatus = async (id, status, rsn) => { try { await api.put(`/admin/sellers/${id}/status`, { status, reason: rsn }); toast.success("Mis à jour"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const productStatus = async (id, status, rsn) => { try { await api.put(`/admin/products/${id}/status`, { status, reason: rsn }); toast.success("Mis à jour"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const featureProduct = async (id) => { await api.put(`/admin/products/${id}/feature`); reload(); };
  const orderStatus = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); reload(); };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); reload(); };
  const saveSettings = async () => { try { await api.put("/admin/settings", { commission_rate: Number(settings.commission_rate) }); toast.success("Commission mise à jour"); } catch (e) { toast.error(errMsg(e)); } };

  const doReject = async () => {
    if (reject.type === "seller") await sellerStatus(reject.id, "rejected", reason);
    else await productStatus(reject.id, "rejected", reason);
    setReject(null); setReason("");
  };

  const tabClass = "over text-[0.6rem] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0A0A0A] data-[state=active]:shadow-none px-0 pb-3 whitespace-nowrap";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
      <div className="flex items-center justify-between mb-8">
        <div><span className="over text-[0.62rem] text-[#D4AF37]">Back-office</span><h1 className="font-display text-4xl font-light mt-1">Administration</h1></div>
        <button onClick={logout} className="over text-[0.62rem] link-underline">Déconnexion</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Stat label="Chiffre d'affaires" value={money(stats.revenue)} accent />
        <Stat label="Commissions" value={money(stats.commission)} />
        <Stat label="Commandes" value={stats.orders} />
        <Stat label="Panier moyen" value={money(stats.aov)} />
        <Stat label="Acheteurs" value={stats.buyers} />
        <Stat label="Vendeurs" value={stats.sellers} />
        <Stat label="Vendeurs en attente" value={stats.pending_sellers} />
        <Stat label="Produits en attente" value={stats.pending_products} />
      </div>

      <Tabs defaultValue="sellers">
        <TabsList className="bg-transparent border-b border-neutral-100 rounded-none w-full justify-start gap-6 p-0 h-auto mb-8 overflow-x-auto no-scrollbar">
          {[["sellers", "Vendeurs"], ["products", "Produits"], ["orders", "Commandes"], ["users", "Utilisateurs"], ["reviews", "Avis"], ["settings", "Commissions"], ["overview", "Performances"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className={tabClass} data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="sellers">
          <div className="space-y-3">
            {sellers.map((s) => (
              <div key={s.id} className="border border-neutral-100 p-4" data-testid={`admin-seller-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-xl">{s.brand_name} <span className="over text-[0.55rem] bg-neutral-100 px-2 py-0.5 ml-2">{SELLER_STATUS[s.status]}</span></p>
                    <p className="text-sm text-neutral-500 mt-1">{s.first_name} {s.last_name} · {s.email} · {s.city}, {s.country}</p>
                    <p className="text-sm text-neutral-600 mt-2">{s.universe}</p>
                    <p className="text-xs text-neutral-400 mt-1">{s.main_category} · {s.product_count} produit(s)</p>
                    {s.sample_images?.length > 0 && <div className="flex gap-2 mt-2">{s.sample_images.map((im, i) => <img key={i} src={im} alt="" className="w-12 h-14 object-cover" />)}</div>}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {s.status !== "approved" && <button onClick={() => sellerStatus(s.id, "approved")} className="over text-[0.56rem] bg-[#0A0A0A] text-white px-3 py-1.5" data-testid={`approve-seller-${s.id}`}>Accepter</button>}
                    {s.status === "pending" && <button onClick={() => { setReject({ type: "seller", id: s.id }); setReason(""); }} className="over text-[0.56rem] border border-neutral-300 px-3 py-1.5">Refuser</button>}
                    {s.status === "approved" && <button onClick={() => sellerStatus(s.id, "suspended")} className="over text-[0.56rem] border border-neutral-300 px-3 py-1.5">Suspendre</button>}
                    {s.status === "suspended" && <button onClick={() => sellerStatus(s.id, "approved")} className="over text-[0.56rem] border border-neutral-300 px-3 py-1.5">Réactiver</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="products">
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border border-neutral-100 p-3" data-testid={`admin-product-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-12 h-14 object-cover bg-neutral-100" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg leading-tight">{p.name} {p.featured && <span className="text-[#D4AF37] text-xs">★</span>}</p>
                  <p className="text-xs text-neutral-500">{p.creator_name} · {p.subcategory} · {money(p.price)} · Stock {p.total_stock}</p>
                </div>
                <span className="over text-[0.55rem] bg-neutral-100 px-2 py-1">{PROD_STATUS[p.status]}</span>
                <div className="flex gap-2 shrink-0">
                  {p.status === "pending" && <button onClick={() => productStatus(p.id, "published")} className="over text-[0.56rem] bg-[#0A0A0A] text-white px-3 py-1.5" data-testid={`approve-product-${p.id}`}>Publier</button>}
                  {p.status === "pending" && <button onClick={() => { setReject({ type: "product", id: p.id }); setReason(""); }} className="over text-[0.56rem] border border-neutral-300 px-3 py-1.5">Refuser</button>}
                  {p.status === "published" && <button onClick={() => featureProduct(p.id)} className="over text-[0.56rem] border border-[#D4AF37] px-3 py-1.5">{p.featured ? "Retirer ★" : "Mettre en avant"}</button>}
                  {p.status === "published" && <button onClick={() => productStatus(p.id, "archived")} className="over text-[0.56rem] text-neutral-400 px-2">Masquer</button>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="border border-neutral-100 p-4 flex items-center justify-between gap-4">
                <div><span className="font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</span><p className="text-sm mt-1">{o.items.length} article(s) · {money(o.total)} · commission {money(o.commission)}</p></div>
                <select value={o.status} onChange={(e) => orderStatus(o.id, e.target.value)} className="border border-neutral-200 text-sm px-2 py-1.5" data-testid={`order-status-${o.id}`}>
                  {ORDER_STATUS.map((s) => <option key={s} value={s}>{ORDER_FR[s]}</option>)}
                </select>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="border border-neutral-100 p-3 flex justify-between text-sm">
                <span>{u.name} · {u.email}</span><span className="over text-[0.55rem] bg-neutral-100 px-2 py-1">{u.role}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <div className="space-y-3">
            {reviews.map((r) => (
              <div key={r.id} className="border border-neutral-100 p-4 flex justify-between gap-4">
                <div><p className="text-sm font-medium">{r.user_name} · {r.rating}/5 · <span className="text-neutral-400">{r.product_name}</span></p><p className="text-sm text-neutral-600 mt-1">{r.comment}</p></div>
                <button onClick={() => delReview(r.id)} className="over text-[0.56rem] text-[#E0115F] shrink-0">Supprimer</button>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-neutral-500">Aucun avis.</p>}
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="max-w-sm">
            <label className="over text-[0.58rem] text-neutral-500">Taux de commission global (%)</label>
            <input type="number" value={settings.commission_rate} onChange={(e) => setSettings({ ...settings, commission_rate: e.target.value })} className="w-full border border-neutral-200 px-3 py-2.5 text-sm mt-2" data-testid="commission-input" />
            <button onClick={saveSettings} className="btn btn-dark mt-4" data-testid="commission-save">Enregistrer</button>
          </div>
        </TabsContent>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <span className="over text-[0.6rem] text-neutral-500">Produits les plus vendus</span>
              <div className="mt-3 space-y-2">{stats.top_products.map((p, i) => <div key={i} className="flex justify-between text-sm border-b border-neutral-50 py-2"><span>{p.name} <span className="text-neutral-400">· {p.creator}</span></span><span>{p.sold} ventes</span></div>)}</div>
            </div>
            <div>
              <span className="over text-[0.6rem] text-neutral-500">Créateurs les plus performants</span>
              <div className="mt-3 space-y-2">{stats.top_creators.map((c, i) => <div key={i} className="flex justify-between text-sm border-b border-neutral-50 py-2"><span>{c.name}</span><span>{money(c.revenue)}</span></div>)}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display text-2xl font-normal">Motif du refus</DialogTitle></DialogHeader>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-neutral-200 p-3 text-sm h-28" placeholder="Expliquez le motif du refus…" data-testid="reject-reason" />
          <button onClick={doReject} className="btn btn-dark" data-testid="reject-confirm">Confirmer le refus</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
